import React, { useState, useRef, useEffect } from 'react';

interface ProjectSelectorProps {
  project: { id: number; name: string } | null;
  projects: Array<{ id: number; name: string }>;
  onSelectProject: (projectId: number | null) => void;
  onOpenCreateProject: () => void;
  onEditProject?: (projectId: number) => void;
}

export const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  project,
  projects,
  onSelectProject,
  onOpenCreateProject,
  onEditProject,
}) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFilter('');
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setFilter('');
      }
    };
    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = filter
    ? projects.filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
    : projects;

  const handleSelect = (projectId: number | null) => {
    onSelectProject(projectId);
    setOpen(false);
    setFilter('');
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`text-sm flex items-center gap-1 transition-colors rounded px-1.5 py-0.5 -mx-1 ${
          project
            ? 'font-semibold text-dark-text hover:text-indigo-400 hover:bg-gray-800'
            : 'text-gray-500 italic hover:text-indigo-400 hover:bg-gray-800'
        }`}
        title={project ? 'Change project' : 'Assign to project'}
      >
        <span className="truncate max-w-[240px]">{project ? project.name : 'Unassigned'}</span>
        <svg className="w-3 h-3 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden">
          {/* Create new project */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setFilter('');
              onOpenCreateProject();
            }}
            className="w-full px-3 py-2.5 text-left text-sm text-indigo-400 hover:bg-gray-700/60 transition-colors flex items-center gap-2 font-medium"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create new project...
          </button>

          <div className="border-t border-gray-700" />

          {/* Filter input */}
          <div className="px-2 py-1.5">
            <input
              ref={inputRef}
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Search projects..."
              className="w-full px-2.5 py-1.5 bg-gray-900 border border-gray-600 rounded text-sm text-gray-200 placeholder:text-gray-500 outline-none focus:border-indigo-500 transition-colors"
            />
          </div>

          {/* Project list */}
          <div className="max-h-[200px] overflow-y-auto">
            {/* Unassigned option */}
            {project && (
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full px-3 py-2 text-left text-sm text-gray-400 italic hover:bg-gray-700/60 transition-colors"
              >
                Unassigned
              </button>
            )}

            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-gray-500 text-center">
                {filter ? 'No projects match' : 'No projects yet'}
              </div>
            )}

            {filtered.map(p => (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSelect(p.id)}
                className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center justify-between group ${
                  project?.id === p.id
                    ? 'text-indigo-400 bg-indigo-600/10'
                    : 'text-gray-200 hover:bg-gray-700/60'
                }`}
              >
                <span className="truncate">{p.name}</span>
                <span className="flex items-center gap-1">
                  {project?.id === p.id && (
                    <svg className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {onEditProject && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpen(false);
                        setFilter('');
                        onEditProject(p.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-600 transition-all text-gray-400 hover:text-gray-200"
                      title="Edit project"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
